import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import NotFoundPage from "./NotFoundPage";
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import projects, { getProjectBySlug } from "../data/projects";
import { caseStudyForProject } from "../data/caseStudies";
import { ProjectPricing } from "../components/sections/ProjectPricing";
import { Tape } from "../components/brand/Doodles";

const setMeta = (attr, key, value) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};
const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};
const injectJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};
const removeJsonLd = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);
  const caseStudy = caseStudyForProject(slug);

  useEffect(() => {
    if (!project) return;
    window.scrollTo(0, 0);
    const canonical = `https://gadgetguymedia.com/projects/${project.slug}`;
    const title = `${project.name} — ${project.tagline} | Gadget Guy Media`;
    document.title = title;
    setMeta("name", "description", project.metaDescription);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", project.metaDescription);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", "article");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", project.metaDescription);
    setLink("canonical", canonical);
    injectJsonLd("jsonld-project-creativework", {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: project.name,
      headline: project.tagline,
      description: project.metaDescription,
      url: canonical,
      sameAs: project.url ? [project.url] : [],
      author: { "@type": "Person", name: "Robert", url: "https://gadgetguymedia.com/robert" },
      publisher: { "@type": "Organization", name: "Gadget Guy Media", url: "https://gadgetguymedia.com/" },
      dateCreated: project.year,
      keywords: [project.category, project.name, ...(project.stack || [])].join(", "),
    });
    injectJsonLd("jsonld-project-breadcrumbs", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://gadgetguymedia.com/" },
        { "@type": "ListItem", position: 2, name: "Projects", item: "https://gadgetguymedia.com/projects" },
        { "@type": "ListItem", position: 3, name: project.name, item: canonical },
      ],
    });
    return () => {
      setLink("canonical", "https://gadgetguymedia.com/");
      removeJsonLd("jsonld-project-creativework");
      removeJsonLd("jsonld-project-breadcrumbs");
    };
  }, [project]);

  if (!project) return <NotFoundPage />;

  const idx = projects.findIndex((p) => p.slug === project.slug);
  const nextProject = projects[(idx + 1) % projects.length];
  const stack = (project.stack || []).slice(0, 8);

  return (
    <PageShell testId="project-detail-page">
      {/* Editorial asymmetric hero */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(48px, 8vh, 96px)",
          paddingBottom: "clamp(48px, 8vh, 96px)",
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(1000px 500px at 20% 30%, rgba(11,60,220,0.14), transparent 70%), radial-gradient(700px 400px at 90% 10%, rgba(40,200,240,0.10), transparent 70%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid gap-8 md:gap-12 lg:grid-cols-[1.05fr_1fr] items-center">
          <div>
            <div className="ggm-eyebrow mb-4" data-testid="project-detail-eyebrow">
              Project / Case Study · {project.category} · {project.year}
            </div>
            <h1
              className="font-condensed font-bold text-white"
              style={{
                fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em",
              }}
              data-testid="project-detail-heading"
            >
              {project.name}
            </h1>
            <p
              className="mt-4 ggm-gradient-text font-condensed font-bold"
              style={{ fontSize: "clamp(1.15rem, 2.4vw, 1.75rem)", lineHeight: 1.2 }}
              data-testid="project-detail-tagline"
            >
              {project.tagline}
            </p>
            <p className="mt-5 font-body text-white/75 text-base md:text-lg leading-relaxed max-w-2xl">
              {project.summary}
            </p>

            {stack.length > 0 && (
              <ul
                className="mt-6 flex flex-wrap gap-2"
                data-testid="project-detail-tech-chips"
              >
                {stack.map((s) => (
                  <li
                    key={s}
                    className="inline-flex items-center font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-full"
                    style={{
                      background: "rgba(230,237,247,0.06)",
                      border: "1px solid rgba(230,237,247,0.14)",
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    {s}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-8 flex flex-wrap gap-3 items-center">
              {project.url ? (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noreferrer"
                  className="ggm-btn-primary"
                  data-testid="project-detail-visit-live"
                >
                  Visit live <ArrowUpRight size={16} />
                </a>
              ) : (
                <Link
                  to="/#start-a-project"
                  className="ggm-btn-primary"
                  data-testid="project-detail-start-project"
                >
                  Start a Project <ArrowUpRight size={16} />
                </Link>
              )}
              {caseStudy && (
                <Link to={caseStudy.path} className="ggm-btn-secondary" data-testid="project-detail-case-study">
                  Read the case study <ArrowRight size={16} />
                </Link>
              )}
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-white/70 hover:text-[var(--ggm-signal-cyan)] transition-colors"
                style={{ transitionDuration: "var(--ggm-duration-ui)", minHeight: 44 }}
                data-testid="project-detail-back-link"
              >
                <ArrowLeft size={14} /> Back to projects
              </Link>
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative">
            {project.image ? (
              <div className="ggm-polaroid relative" style={{ transform: "rotate(1.5deg)" }} data-testid="project-detail-polaroid">
                <Tape />
                <img
                  src={project.image}
                  alt={`${project.name} — ${project.tagline}`}
                  className="w-full h-auto block"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                {project.url && (
                  <span className="ggm-hand block pt-2 text-center text-base" style={{ color: "var(--ggm-navy)" }}>
                    {project.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </span>
                )}
              </div>
            ) : (
              <div
                className="rounded-2xl flex items-center justify-center"
                style={{
                  aspectRatio: "16/10",
                  background:
                    "linear-gradient(135deg, rgba(11,60,220,0.35), rgba(40,200,240,0.18) 60%, rgba(11,30,58,0.9) 100%)",
                  border: "1px solid var(--ggm-border-subtle)",
                }}
              >
                <span
                  className="font-condensed font-bold text-white/90"
                  style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", letterSpacing: "-0.02em" }}
                >
                  {project.name}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Problem + What I Built — editorial two-column */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid gap-10 md:gap-16 md:grid-cols-2">
          <div data-testid="project-detail-problem">
            <div className="ggm-eyebrow mb-4">The Problem</div>
            <p className="font-body text-white/80 text-base md:text-lg leading-relaxed">
              {project.problem}
            </p>
          </div>
          <div data-testid="project-detail-solution">
            <div className="ggm-eyebrow mb-4">What I Built</div>
            <p className="font-body text-white/80 text-base md:text-lg leading-relaxed">
              {project.solution}
            </p>
          </div>
        </div>
      </section>

      {/* How it works / Features — only if data exists */}
      {project.highlights && project.highlights.length > 0 && (
        <section
          className="relative"
          style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
          data-testid="project-detail-highlights"
        >
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="ggm-eyebrow mb-6">How It Works</div>
            <ul className="grid gap-5 md:grid-cols-2">
              {project.highlights.map((h, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 pl-5 py-1"
                  style={{ borderLeft: "2px solid var(--ggm-signal-cyan)" }}
                >
                  <CheckCircle2
                    size={18}
                    className="shrink-0 mt-1"
                    style={{ color: "var(--ggm-signal-cyan)" }}
                    aria-hidden
                  />
                  <span className="font-body text-white/85 text-sm md:text-base leading-relaxed">
                    {h}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Tech + Outcome */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid gap-10 md:gap-16 md:grid-cols-[1fr_1.4fr]">
          <div data-testid="project-detail-stack">
            <div className="ggm-eyebrow mb-4">Tech / AI</div>
            <div className="flex flex-wrap gap-2">
              {(project.stack || []).map((s) => (
                <span
                  key={s}
                  className="font-condensed uppercase tracking-widest text-[11px] text-white/70 px-3 py-1.5 rounded-full"
                  style={{
                    background: "rgba(40,200,240,0.06)",
                    border: "1px solid var(--ggm-border-subtle)",
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
          <div data-testid="project-detail-outcome">
            <div className="ggm-eyebrow mb-4">The Outcome</div>
            <p className="font-body text-white/85 text-base md:text-lg leading-relaxed">
              {project.outcome}
            </p>
          </div>
        </div>
      </section>

      <ProjectPricing project={project} />

      {/* Next build + View all */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div
            className="flex items-center justify-between gap-4 py-6 border-t"
            style={{ borderColor: "var(--ggm-border-subtle)" }}
          >
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-white/60 hover:text-[var(--ggm-signal-cyan)] transition-colors"
              style={{ transitionDuration: "var(--ggm-duration-ui)", minHeight: 44 }}
              data-testid="project-detail-view-all"
            >
              <ArrowLeft size={13} /> View all projects
            </Link>
            {nextProject && nextProject.slug !== project.slug && (
              <Link
                to={`/projects/${nextProject.slug}`}
                className="group flex items-center gap-3 text-right"
                data-testid="project-detail-next"
              >
                <div>
                  <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
                    Next build
                  </div>
                  <div
                    className="font-condensed font-bold text-white group-hover:text-[var(--ggm-signal-cyan)] transition-colors"
                    style={{ fontSize: "clamp(1rem, 2.2vw, 1.35rem)" }}
                  >
                    {nextProject.name}
                  </div>
                </div>
                <ArrowRight
                  size={22}
                  className="text-white/40 group-hover:text-[var(--ggm-signal-cyan)] group-hover:translate-x-0.5 transition-all duration-300"
                />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Compact conversion block */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(48px, 8vh, 96px)",
          paddingBottom: "clamp(64px, 10vh, 140px)",
          background:
            "radial-gradient(1000px 500px at 30% 20%, rgba(11,60,220,0.14), transparent 70%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2
            className="font-condensed font-bold text-white uppercase"
            style={{
              fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            Have an idea of your own?
          </h2>
          <p
            className="mt-3 font-condensed font-bold ggm-gradient-text uppercase"
            style={{ fontSize: "clamp(1.15rem, 2.5vw, 1.75rem)", lineHeight: 1.15 }}
          >
            You Imagine It. I Build It.
          </p>
          <div className="mt-7 flex justify-center">
            <Link
              to="/#start-a-project"
              className="ggm-btn-primary"
              data-testid="project-detail-cta"
            >
              Start a Project <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
